/*
  Gender-aware search hub.

  Decoupled from predictive-search.js on purpose: it only reads and writes the
  DOM, so it works alongside whatever that custom element already does.

  Responsibilities
    1. Show the [data-search-gender] blocks matching body[data-gender], live.
    2. Header (hub has no data-hub-mode): show the predictions list once the
       field has a query. The hub itself stays visible the whole time.
       A pre-filled value does not open predictions until the shopper types
       or a response actually renders.
    3. Page hub (data-hub-mode="on-type"): keep the hub and the predictions
       hidden on load, even when the input is already filled with search.terms.
       After a real input event on that field, a non-empty value shows both,
       and clearing the field hides predictions and shows the hub.

  Product lists are not filtered here. gender-products.js makes Shopify
  return only the active gender. Dropping cards after they arrive still
  pays for the other catalog.
*/
(function () {
  'use strict';

  var DEFAULT_GENDER = 'mujer';
  var MAX_PREDICTIONS = 3;
  var ON_TYPE = 'on-type';

  // Products with no Gender_* tag show for every gender rather than vanishing.
  var SHOW_UNTAGGED_PRODUCTS = true;

  function canonicalGender(value) {
    var text = String(value || '').trim().toLowerCase();
    if (text === 'hombre' || text === 'men' || text === 'gender_hombre' || text === 'gender-hombre') return 'hombre';
    if (text === 'mujer' || text === 'women' || text === 'gender_mujer' || text === 'gender-mujer') return 'mujer';
    return text;
  }

  function currentGender() {
    var stored = '';
    try {
      stored = sessionStorage.getItem('selectedGender') || '';
    } catch (error) {
      stored = '';
    }

    var fromStore = canonicalGender(stored);
    if (fromStore === 'hombre' || fromStore === 'mujer') return fromStore;

    var fromBody = document.body && document.body.getAttribute('data-gender');
    var fromRoot = document.documentElement && document.documentElement.getAttribute('data-gender');
    var fromDom = canonicalGender(fromBody || fromRoot);
    if (fromDom === 'hombre' || fromDom === 'mujer') return fromDom;

    return DEFAULT_GENDER;
  }

  function matchesGender(element, gender) {
    var value = (element.getAttribute('data-product-gender') || '').trim().toLowerCase();
    if (value === '') return SHOW_UNTAGGED_PRODUCTS;
    return value.split(/\s+/).some(function (token) {
      return canonicalGender(token) === gender;
    });
  }

  function applyGenderToSections(root, gender) {
    var wanted = canonicalGender(gender);
    root.querySelectorAll('[data-search-gender]').forEach(function (section) {
      section.hidden = canonicalGender(section.getAttribute('data-search-gender')) !== wanted;
    });
  }

  /*
    Kept so older callers do not throw. It does not remove products.
    The request already asked Shopify for one gender.
  */
  function filterPredictions(container) {
    if (!container) return;
    container.dispatchEvent(
      new CustomEvent('predictive-search:filtered', {
        bubbles: true,
        detail: { gender: currentGender(), kept: container.querySelectorAll('li[data-product-gender]').length }
      })
    );
  }

  function findInput(scope) {
    return scope.querySelector('input[type="search"]');
  }

  function onTypeHub(scope) {
    var hub = scope.querySelector('[data-search-hub]');
    if (!hub || hub.getAttribute('data-hub-mode') !== ON_TYPE) return null;
    return hub;
  }

  /*
    Header: only the predictions list is toggled, so predictions stack above
    trending / recent / popular / curated rather than replacing them.

    Page (on-type): nothing is shown until this field fires a real input
    event. After that, predictions and the hub show together while the field
    has text, and an empty field shows the hub alone.
  */
  function syncPredictions(scope) {
    var predictions = scope.querySelector('[data-predictive-search]');
    var hub = onTypeHub(scope);
    var input = findInput(scope);
    var hasQuery = !!(input && input.value.trim() !== '');

    if (hub) {
      if (scope.getAttribute('data-search-hub-typed') !== 'true') {
        if (predictions) predictions.hidden = true;
        hub.hidden = true;
        return;
      }

      if (predictions) predictions.hidden = !hasQuery;
      hub.hidden = false;
      return;
    }

    if (!predictions) return;
    predictions.hidden = !hasQuery;
  }

  function setupScope(scope) {
    if (scope.dataset.genderSearchReady === 'true') return;
    scope.dataset.genderSearchReady = 'true';

    var gender = currentGender();
    applyGenderToSections(scope, gender);

    /*
      On /search the input arrives pre-filled with search.terms. Treating that
      as a live query would reveal a predictions panel holding nothing but the
      loading spinner. Start hidden. The header opens on a real input or once
      a response renders. The page hub (data-hub-mode="on-type") stays hidden
      until the shopper actually types in that field.
    */
    var initialPredictions = scope.querySelector('[data-predictive-search]');
    if (initialPredictions) initialPredictions.hidden = true;

    var pageHub = onTypeHub(scope);
    if (pageHub) {
      scope.removeAttribute('data-search-hub-typed');
      pageHub.hidden = true;
    }

    var input = findInput(scope);

    if (input) {
      input.addEventListener('input', function () {
        if (onTypeHub(scope)) scope.setAttribute('data-search-hub-typed', 'true');
        syncPredictions(scope);
      });

      if (input.form) {
        input.form.addEventListener('reset', function () {
          // Runs before the browser clears the field.
          window.setTimeout(function () {
            syncPredictions(scope);
          }, 0);
        });
      }
    }

    var predictions = scope.querySelector('[data-predictive-search]');

    if (predictions) {
      // Filtering removes nodes, which would re-trigger this observer.
      var filtering = false;

      new MutationObserver(function () {
        if (filtering) return;
        filtering = true;

        try {
          syncPredictions(scope);
        } finally {
          filtering = false;
        }
      }).observe(predictions, { childList: true, subtree: true });
    }
  }

  function setupAll() {
    document.querySelectorAll('predictive-search, search-form').forEach(setupScope);
  }

  function onGenderChange() {
    var gender = currentGender();

    document.querySelectorAll('predictive-search, search-form').forEach(function (scope) {
      applyGenderToSections(scope, gender);
    });
  }

  function init() {
    new MutationObserver(onGenderChange).observe(document.body, {
      attributes: true,
      attributeFilter: ['data-gender']
    });

    setupAll();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', setupAll);

  window.SearchGender = {
    currentGender: currentGender,
    matchesGender: matchesGender,
    filterPredictions: filterPredictions,
    applyGenderToSections: applyGenderToSections,
    syncPredictions: syncPredictions,
    setupScope: setupScope,
    MAX_PREDICTIONS: MAX_PREDICTIONS
  };
})();
